import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { IUserAvatar, IReduxRequestNode } from "@litres-libs/types";
import { Request } from "../../request/Request";
import { addHydrateReducer } from "../../utils/hydrateReducer";

export interface IUserAvatarState extends IUserAvatar {
  actions: {
    removeAvatar: IReduxRequestNode<
      IRemoveUserAvatarRequest,
      IRemoveUserAvatarResponse
    >;
    uploadAvatarCSRF: IReduxRequestNode<
      IUploadAvatarRequestCSRF,
      IUploadAvatarResponseCSRF
    >;
  };
}

const calculateAvatarHash = (): string =>
  Math.round(Math.random() * 1000).toString();

const initialState: IUserAvatarState = {
  availability: false,
  avatarHash: calculateAvatarHash(),
  actions: {
    removeAvatar: {},
    uploadAvatarCSRF: {},
  },
};

interface IUploadAvatarRequestCSRF {
  avatar: File;
  action: "store_user_avatar";
}

interface IUploadAvatarResponseCSRF {
  actions_output: {
    message: number;
  };
  status: string;
}

type TUploadAvatarCSRFNode = IReduxRequestNode<
  IUploadAvatarRequestCSRF,
  IUploadAvatarResponseCSRF
>;

interface IUploadAvatarParamsCSRF {
  avatar: File;
}

export const uploadAvatarCSRF = createAsyncThunk(
  "user/avatar/uploadAvatarCSRF",
  async (params: {
    data: IUploadAvatarParamsCSRF;
    onSuccess?: (success: TUploadAvatarCSRFNode) => void;
    onError?: (error: TUploadAvatarCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IUploadAvatarRequestCSRF,
      IUploadAvatarResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "uploadAvatarCSRF",
      },
      request: {
        method: "POST",
        data: {
          action: "store_user_avatar",
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export interface IRemoveUserAvatarRequest {
  // TODO: Выяснить запрос
}

export interface IRemoveUserAvatarResponse {
  // TODO: Выяснить что тут кроме стандартного ответа
}

export const removeAvatar = createAsyncThunk(
  "user/avatar/removeAvatar",
  async () => {
    const response = new Request.Cat2<
      IRemoveUserAvatarRequest,
      IRemoveUserAvatarResponse
    >({
      url: "/",
      provider: {
        id: "w_userpic_remove",
      },
      request: {
        method: "POST",
      },
    });

    return await response.fetch();
  }
);

export const avatarSlice = createSlice({
  name: "user/avatar",
  initialState,
  reducers: {
    setUserAvatar: (
      state: IUserAvatarState,
      action: PayloadAction<Partial<IUserAvatar>>
    ) => {
      const { availability } = action.payload;

      if (typeof availability === "boolean") {
        state.availability = availability;
      }
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "user.avatar");
    /**
     * Загрузка аватара юзера
     */
    builder.addCase(uploadAvatarCSRF.pending, (state: IUserAvatarState) => {
      state.actions.uploadAvatarCSRF.isLoading = true;
      state.actions.uploadAvatarCSRF.error = undefined;
    });

    builder.addCase(
      uploadAvatarCSRF.fulfilled,
      (
        state: IUserAvatarState,
        action: PayloadAction<
          IReduxRequestNode<IUploadAvatarRequestCSRF, IUploadAvatarResponseCSRF>
        >
      ) => {
        state.actions.uploadAvatarCSRF.isLoading = false;
        state.avatarHash = calculateAvatarHash();

        if (action.payload.error) {
          console.error(
            "Ошибка запроса uploadAvatarCSRF",
            action.payload.error
          );
          state.actions.uploadAvatarCSRF.error = action.payload.error;
        }
      }
    );

    /**
     * Удаление аватара юзера
     */
    builder.addCase(removeAvatar.pending, (state: IUserAvatarState) => {
      state.actions.removeAvatar.isLoading = true;
      state.actions.removeAvatar.error = undefined;
    });
    builder.addCase(
      removeAvatar.fulfilled,
      (
        state: IUserAvatarState,
        action: PayloadAction<
          IReduxRequestNode<IRemoveUserAvatarRequest, IRemoveUserAvatarResponse>
        >
      ) => {
        state.actions.removeAvatar.isLoading = false;
        state.actions.removeAvatar = action.payload;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error("Ошибка запроса removeAvatar", action.payload.error);
          state.actions.removeAvatar.error = action.payload.error;
        }
      }
    );
  },
});

export const { setUserAvatar } = avatarSlice.actions;

export default avatarSlice.reducer;
